import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import Api from "../Api";
import Account from "../helpers/Account";
import { lang, login } from "../translate";
import Cookies from "js-cookie";
import axios from "axios";
import LogRegSide from "../components/LogRegSide";
import qs from "query-string";
import { IMaskInput } from "react-imask";
import { useDispatch } from "react-redux";
import { logoutAdmin } from "../store/actions/users";
import { PROMOCODE_MODAL_DISMISSED_KEY } from "../components/new_component/PromocodeModal";
const { REACT_APP_SERVER } = process.env;

function Login(props) {
    // unified: phone or email
    const [loginId, setLoginId] = useState("");
    const [isPhoneMode, setIsPhoneMode] = useState(false); // start text mode
    const [password, setPassword] = useState("");
    const [ip, setIp] = useState("");
    const [show, setShow] = useState(false);
    const [lang1, setLang] = useState(1);
    const [loading, setLoading] = useState(false);

    // refs to restore focus when toggling
    const textRef = useRef(null);
    const maskRef = useRef(null);
    const dispatch = useDispatch()
    useEffect(() => {
        Cookies.remove('buddy');
        Cookies.remove('key');
        Cookies.remove('viewer');
        Cookies.remove('member');
        Cookies.remove('login');
        Cookies.remove('new');
        Cookies.remove('original');
        sessionStorage.removeItem(PROMOCODE_MODAL_DISMISSED_KEY);
        dispatch(logoutAdmin())
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const query = qs.parse(location.search, { arrayFormat: "comma" });

    // ----------------------------
    // helpers
    // ----------------------------
    const normalizeDigits = (v = "") =>
        String(v).trim().replace(/\s/g, "").replace(/[()+-]/g, "");

    const detectIsPhone = (raw = "") => {
        const v = normalizeDigits(raw);
        if (!v) return false;
        return /^\d+$/.test(v);
    };

    const detectIsEmailHint = (raw = "") => String(raw).includes("@");

    // focus newly mounted input after toggle
    useEffect(() => {
        const t = setTimeout(() => {
            const el = isPhoneMode ? maskRef.current : textRef.current;
            if (el && typeof el.focus === "function") {
                el.focus();
                // keep cursor at end (helps after remount)
                if (typeof el.setSelectionRange === "function") {
                    const len = el.value?.length ?? 0;
                    el.setSelectionRange(len, len);
                }
            }
        }, 0);

        return () => clearTimeout(t);
    }, [isPhoneMode]);

    const handleLoginIdChange = (valueOrEvent) => {
        const value =
            typeof valueOrEvent === "string"
                ? valueOrEvent
                : valueOrEvent?.target?.value ?? "";

        setLoginId(value);

        // If user types/pastes an email => email mode
        if (detectIsEmailHint(value)) {
            setIsPhoneMode(false);
            return;
        }

        // If user types digits only => phone mode
        if (detectIsPhone(value)) {
            setIsPhoneMode(true);
            return;
        }

        // Otherwise keep text mode (email possible)
        setIsPhoneMode(false);
    };

    const handleSubmit = useCallback(
        async (ev) => {
            try {
                ev.preventDefault();

                if (!loginId || !password.trim()) {
                    toast.error("Enter phone/email and password");
                    return;
                }

                const isPhone = detectIsPhone(loginId) && !detectIsEmailHint(loginId);

                // validate phone fully (Armenia: 11 digits like 374XXXXXXXX)
                if (isPhone) {
                    const digits = normalizeDigits(loginId);
                    if (digits.length !== 11) {
                        toast.error("Enter full phone number (e.g. 374XXXXXXXX)");
                        return;
                    }
                } else {
                    // basic email validation
                    const email = String(loginId).trim();
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        toast.error("Enter a valid email");
                        return;
                    }
                }

                setLoading(true);

                const payload = isPhone
                    ? {
                        phone: normalizeDigits(loginId),
                        password,
                        lang: lang[+localStorage.getItem("atsLang")],
                    }
                    : {
                        username: String(loginId).trim().toLowerCase(),
                        password,
                        lang: lang[+localStorage.getItem("atsLang")],
                    };

                if (REACT_APP_SERVER === "Beeline") {
                    const { data } = await Api.login(payload);
                    Account.setAdminToken(data);
                    navigate(`/`);
                } else {
                    // Keep your special admin phone shortcut ONLY when phone
                    if (isPhone && normalizeDigits(loginId) === "37455555555") {
                        const { data } = await Api.login({
                            phone: "37455555555",
                            password: "admin12345",
                            lang: lang[+localStorage.getItem("atsLang")],
                        });
                        Account.setAdminToken(data);
                        navigate(`/`);
                    } else {

                        if (isPhone) {
                            await Api.login(payload);
                            Cookies.set("login", "progress", { expires: 1 });
                            navigate(`/confirm/user/${normalizeDigits(loginId)}`);
                        } else {
                            await Api.loginEmployee(payload);

                            navigate(`/confirm/employee/${String(loginId).trim()}`);
                        }
                    }
                }

                setLoading(false);
            } catch (e) {
                toast.error(e?.response?.data?.message || "Login failed");
                setLoading(false);
            }
        },
        [loginId, password, navigate]
    );

    useEffect(() => {
        Account.logoutAdmin();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get("https://api.ipify.org?format=json");
                setIp(data.ip);
            } catch (e) { }

            if (query.phone === "37455555555") {
                try {
                    const { data } = await Api.login({
                        phone: query.phone,
                        password: "admin12345",
                        lang: lang[+localStorage.getItem("atsLang")],
                    });
                    Account.setAdminToken(data);
                    navigate(`/`);
                } catch (e) { }
            }
        })();
    }, [query.phone, navigate]);

    const handleLangChange = useCallback((lng) => {
        setLang(lng);
        localStorage.setItem("atsLang", lng);
    }, []);

    return (
        <>
            <Helmet>
                <title>{login.title[lang1]}</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="adminLogin">
                <div
                    className="loginContainer"
                    style={REACT_APP_SERVER === "Beeline" ? { borderRadius: 20 } : {}}
                >
                    <div className="adminLoginHeader">
                        <div>
                            <div className="d-flex flex-row">
                                <a className="loginLink" href={"/operators/login"}>
                                    Operator
                                </a>
                                <span className="my-border" />{" "}
                                <a className="loginLink" href={"/login"}>
                                    Admin
                                </a>
                            </div>
                            {REACT_APP_SERVER !== "Beeline" ? <p>IP: {ip}</p> : null}
                        </div>

                        <div>
                            <span
                                style={{ cursor: "pointer", fontSize: 13, color: "#039be5" }}
                                onClick={() => handleLangChange(3)}
                            >
                                Հայ
                            </span>
                            <span className="my-border" />
                            <span
                                style={{ cursor: "pointer", fontSize: 13, color: "#039be5" }}
                                onClick={() => handleLangChange(2)}
                            >
                                Рус
                            </span>
                            <span className="my-border" />
                            <span
                                style={{ cursor: "pointer", fontSize: 13, color: "#039be5" }}
                                onClick={() => handleLangChange(1)}
                            >
                                Eng
                            </span>
                        </div>
                    </div>

                    <div className="adminLoginPage">
                        <form onSubmit={handleSubmit} className="adminLoginForm">
                            <div className="adminLoginMain">
                                {/* Smart toggle input + auto focus on toggle */}
                                <label className="adminInput">
                                    {isPhoneMode ? (
                                        <IMaskInput
                                            key="phone"
                                            inputRef={maskRef} // ✅ focusable input ref
                                            mask="374 (00) 000000"
                                            definitions={{ "0": /[0-9]/ }}
                                            autoComplete="tel"
                                            unmask={false}
                                            value={loginId}
                                            onAccept={(value) => handleLoginIdChange(value)}
                                            placeholder="374 (__) ______"
                                            inputMode="numeric"
                                        />
                                    ) : (
                                        <input
                                            key="text"
                                            ref={textRef}
                                            type="text"
                                            autoComplete="username"
                                            value={loginId}
                                            onChange={handleLoginIdChange}
                                            placeholder={login.phoneOrEmail[lang1]}
                                            autoFocus
                                        />
                                    )}
                                </label>

                                <label
                                    htmlFor="adminPass"
                                    style={{ display: "flex" }}
                                    className="adminFormLabel"
                                >
                                    <input
                                        id="adminPass"
                                        value={password}
                                        placeholder={login.pass[lang1]}
                                        type={show ? "text" : "password"}
                                        onChange={(ev) => setPassword(ev.target.value)}
                                    />
                                    {show ? (
                                        <VisibilityIcon
                                            fontSize="small"
                                            onClick={() => setShow(false)}
                                        />
                                    ) : (
                                        <VisibilityOffIcon
                                            fontSize="small"
                                            onClick={() => setShow(true)}
                                        />
                                    )}
                                </label>

                                <button
                                    type="submit"
                                    onClick={(ev) => (loading ? null : handleSubmit(ev))}
                                    className="adminLoginBtn"
                                >
                                    {loading ? "Loading..." : login.title[lang1]}
                                </button>

                                {REACT_APP_SERVER === "V-office" ||
                                    REACT_APP_SERVER === "Ucom" ? (
                                    <span style={{ marginBottom: 4 }}>
                                        {login.drop[lang1]}
                                        <Link to="/drop_pass">{login.dropLink[lang1]}</Link>
                                    </span>
                                ) : null}

                                {REACT_APP_SERVER === "V-office" ||
                                    REACT_APP_SERVER === "Ucom" ? (
                                    <span>
                                        {login.reg[lang1]}{" "}
                                        <Link to="/registration">{login.regLink[lang1]}</Link>
                                    </span>
                                ) : null}
                            </div>
                        </form>
                    </div>
                </div>

                {REACT_APP_SERVER === "Beeline" ? null : <LogRegSide />}
            </div>
        </>
    );
}

export default Login;
